import { useEffect, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import useGlobalContext from "hooks/globalData";
import { useQuery } from "react-query";
import KpiTile from "./components/KpiTile";
import { monthKey, normalizeMonths, safeHttpUrl } from "./monthUtils";

const MEDALS = ["🥇", "🥈", "🥉"];
// Positional category palette (blue, light-blue, red, pink, …) applied after a
// total-desc sort — mirrors the Streamlit donut's count-ordered color sequence.
const CATEGORY_COLORS = ["#2563eb", "#93c5fd", "#dc2626", "#f9a8d4", "#16a34a", "#f59e0b", "#7c3aed", "#0d9488", "#ea580c", "#64748b"];
const CHART_CARD_SX = { p: 2, borderRadius: 2, borderColor: "divider", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" };
const MONTH_ABBR = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TREND_ICON: Record<string, string> = { up: "📈", down: "📉", flat: "➡️" };
const STATUS_EMOJI: Record<string, string> = { Done: "✅", "In Progress": "🔄", Backlog: "⏳" };

const formatMonthKey = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_ABBR[m] ?? m} ${y}`;
};

const BugsTab = () => {
  const { developerDashboardService } = useGlobalContext();

  const monthsQuery = useQuery("dashboardMonths", () => developerDashboardService.getAvailableMonths());
  const months = normalizeMonths(monthsQuery.data?.data);

  const [selectedKey, setSelectedKey] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[] | null>(null);
  const [compareMonths, setCompareMonths] = useState<string[]>([]);
  const [bugSearch, setBugSearch] = useState<string>("");

  const lastMonth = months.at(-1);
  const latest = lastMonth ? monthKey(lastMonth) : "";
  const effectiveKey = selectedKey || latest;
  const [year, month] = effectiveKey ? effectiveKey.split("-").map(Number) : [0, 0];

  // Drop the selected month from the compare set so switching to a month already picked for
  // comparison never produces a self-comparison in the trend.
  useEffect(() => {
    setCompareMonths((prev) => prev.filter((k) => k !== effectiveKey));
  }, [effectiveKey]);

  const bugsQuery = useQuery(
    ["dashboardBugs", year, month, selectedCategories],
    () => developerDashboardService.getBugs(year, month, selectedCategories),
    { enabled: year > 0, keepPreviousData: true }
  );
  const data = bugsQuery.data?.data;

  const trendQuery = useQuery(
    ["dashboardTrend", year, month, compareMonths, selectedCategories],
    () => developerDashboardService.getBugsTrend(year, month, compareMonths, selectedCategories),
    { enabled: year > 0 && compareMonths.length > 0, keepPreviousData: true }
  );
  const trend = trendQuery.data?.data;

  if (monthsQuery.isLoading || (year > 0 && bugsQuery.isLoading)) {
    return (
      <Box display="flex" justifyContent="center" my={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (monthsQuery.isError) {
    return <Alert severity="error">Failed to load available months.</Alert>;
  }

  if (bugsQuery.isError) {
    return <Alert severity="error">Failed to load bug metrics.</Alert>;
  }

  const kpi = data?.kpi;
  // Normalize the optional NSwag DTO into a chart-ready view-model once, so the
  // chart (which requires number[]) and the table both read clean fields.
  const breakdown = (data?.categoryBreakdown ?? [])
    .map((b) => ({
      category: b.category ?? "",
      total: b.total ?? 0,
      done: b.done ?? 0,
      inProgress: b.inProgress ?? 0,
      backlog: b.backlog ?? 0,
      resolutionPct: b.resolutionPct ?? 0,
    }))
    .sort((a, b) => b.total - a.total);
  const breakdownTotal = breakdown.reduce((sum, b) => sum + b.total, 0) || 1;
  const ranking = data?.devRanking ?? [];
  const summary = data?.assignmentSummary;
  const rows = data?.rows ?? [];

  // Full canonical category list (backend returns it regardless of the active
  // filter, so the picker never collapses). null selection means "all".
  const availableCategories = data?.availableCategories ?? [];
  const categoryValue = selectedCategories ?? availableCategories;
  // An explicit empty selection returns no rows; say so plainly instead of the ambiguous
  // "no bugs this month", which reads like a data problem.
  const emptyMessage =
    selectedCategories?.length === 0
      ? "No categories selected — pick at least one category to see bugs."
      : "No bugs found for this month.";

  const handleCategoryChange = (value: string | string[]) => {
    const next = typeof value === "string" ? value.split(",") : value;
    setSelectedCategories(next.length === availableCategories.length ? null : next);
  };

  // All these are pure projections of the rows the backend already returns.
  const byCreatedDesc = (a: { created?: Date }, b: { created?: Date }) => (b.created?.getTime() ?? 0) - (a.created?.getTime() ?? 0);
  const criticalBugs = rows.filter((b) => b.priority === "Emergency" && b.status !== "Done");
  const pendingBugs = [...rows.filter((b) => b.assignee === "Unassigned")].sort(byCreatedDesc);
  const noChangeBugs = [...rows.filter((b) => b.assignee === "No Code Change")].sort(byCreatedDesc);

  const fmtDate = (d?: Date) => (d ? new Date(d).toLocaleDateString("en-CA") : "");
  const bugSearchLc = bugSearch.trim().toLowerCase();
  const displayRows = [...rows]
    .filter((b) => !bugSearchLc || [b.key, b.summary, b.assignee].some((f) => (f ?? "").toLowerCase().includes(bugSearchLc)))
    .sort(byCreatedDesc);

  const downloadBugsCsv = () => {
    const cell = (v: string | undefined = "") => {
      let s = v;
      // Neutralize spreadsheet formula injection: a leading =, +, -, @ (or tab/CR) can execute
      // when the CSV is opened in Excel/Sheets, so prefix risky cells with a single quote.
      if (/^[=+\-@\t\r]/.test(s)) {
        s = `'${s}`;
      }
      return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
    };
    const header = ["Key", "Summary", "Category", "Status", "Priority", "Created", "Resolved", "Assignee"];
    const lines = displayRows.map((b) =>
      [b.key, b.summary, b.category, b.status, b.priority, fmtDate(b.created), fmtDate(b.resolved), b.assignee].map(cell).join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kone-bugs-${(data?.monthLabel ?? "").toLowerCase().replace(/\s+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Trend-derived view-models (only meaningful when compare months are picked).
  const trendCats = (trend?.rows ?? []).filter((r) => !r.isTotal);
  const trendMonths = trend?.months ?? [];
  const insights = trendCats.filter((r) => Math.abs(r.pctChange ?? 0) > 50).slice(0, 5);
  const resPoints = trend?.resolutionTrend ?? [];
  const resMonths = Array.from(new Set(resPoints.map((p) => p.month ?? ""))).filter(Boolean).sort((a, b) => a.localeCompare(b));
  const resCats = Array.from(new Set(resPoints.map((p) => p.category ?? ""))).filter(Boolean);
  const resLookup: Record<string, Record<string, number>> = {};
  resPoints.forEach((p) => {
    const c = p.category ?? "";
    const m = p.month ?? "";
    resLookup[c] ??= {};
    resLookup[c][m] = p.resolutionPct ?? 0;
  });

  const bugLine = (b: { key?: string; summary?: string; status?: string; priority?: string; jiraUrl?: string; ticketAssignee?: string }) => (
    <Box component="li" key={b.key} sx={{ mb: 0.5 }}>
      {STATUS_EMOJI[b.status ?? ""] ?? "•"}{" "}
      <Link href={safeHttpUrl(b.jiraUrl)} target="_blank" rel="noopener">
        {b.key}
      </Link>{" "}
      — {b.summary}{" "}
      <Typography component="span" variant="caption" color="text.secondary">
        ({b.status} · {b.priority}
        {b.ticketAssignee && b.ticketAssignee !== "Unassigned" ? ` · BA: ${b.ticketAssignee}` : ""})
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={effectiveKey} onChange={(e) => setSelectedKey(String(e.target.value))}>
            {[...months].reverse().map((m) => (
              <MenuItem key={monthKey(m)} value={monthKey(m)}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }} disabled={availableCategories.length === 0}>
          <InputLabel>Filter Categories</InputLabel>
          <Select
            multiple
            value={categoryValue}
            onChange={(e) => handleCategoryChange(e.target.value)}
            input={<OutlinedInput label="Filter Categories" />}
            renderValue={(sel) => (sel.length === availableCategories.length ? "All categories" : `${sel.length} selected`)}
          >
            {availableCategories.map((c) => (
              <MenuItem key={c} value={c}>
                <Checkbox checked={categoryValue.includes(c)} />
                <ListItemText primary={c} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Compare Against</InputLabel>
          <Select
            multiple
            value={compareMonths}
            onChange={(e) => setCompareMonths(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
            input={<OutlinedInput label="Compare Against" />}
            renderValue={(sel) => (sel.length === 0 ? "None" : `${sel.length} month(s)`)}
          >
            {[...months]
              .reverse()
              .filter((m) => monthKey(m) !== effectiveKey)
              .map((m) => {
                const key = monthKey(m);
                return (
                  <MenuItem key={key} value={key}>
                    <Checkbox checked={compareMonths.includes(key)} />
                    <ListItemText primary={m.label} />
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Stack>

      {!data || rows.length === 0 ? (
        <Alert severity="info">{emptyMessage}</Alert>
      ) : (
        <>
          <Typography variant="h6" gutterBottom>
            {data.monthLabel} — KONE Bug Report
          </Typography>

          {kpi && (
            <Grid container columns={10} spacing={2} mb={3}>
              <Grid item xs={5} md={2}>
                <KpiTile label="Total Bugs" value={kpi.total} delta={kpi.totalDelta} invertDelta />
              </Grid>
              <Grid item xs={5} md={2}>
                <KpiTile label="Resolved" value={`${kpi.done} (${kpi.resolutionPct}%)`} delta={kpi.resolutionPctDelta} deltaSuffix="%pts" />
              </Grid>
              <Grid item xs={5} md={2}>
                <KpiTile label="In Progress" value={kpi.inProgress} delta={kpi.inProgressDelta} invertDelta />
              </Grid>
              <Grid item xs={5} md={2}>
                <KpiTile label="Backlog" value={kpi.backlog} delta={kpi.backlogDelta} invertDelta />
              </Grid>
              <Grid item xs={5} md={2}>
                <KpiTile label="Critical Open" value={kpi.criticalOpen} delta={kpi.criticalOpenDelta} invertDelta />
              </Grid>
            </Grid>
          )}

          {criticalBugs.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Open Critical Bugs — Requires Attention</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {criticalBugs.map((b) => (
                  <Box component="li" key={b.key}>
                    <Link href={safeHttpUrl(b.jiraUrl)} target="_blank" rel="noopener">
                      {b.key}
                    </Link>{" "}
                    — {b.summary}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          <Typography variant="h6" gutterBottom>
            Bug Distribution by Category
          </Typography>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={CHART_CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Bugs by Category &amp; Status
                </Typography>
                {breakdown.length > 0 && (
                  <BarChart
                    height={Math.max(240, 40 + breakdown.length * 34)}
                    layout="horizontal"
                    yAxis={[{ scaleType: "band", data: breakdown.map((b) => b.category) }]}
                    series={[
                      { data: breakdown.map((b) => b.done), label: "Done", stack: "s", color: "#16a34a" },
                      { data: breakdown.map((b) => b.backlog), label: "Backlog", stack: "s", color: "#ca8a04" },
                      { data: breakdown.map((b) => b.inProgress), label: "In Progress", stack: "s", color: "#0284c7" },
                    ]}
                    margin={{ left: 130 }}
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={CHART_CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Category Share
                </Typography>
                {breakdown.length > 0 && (
                  <PieChart
                    height={370}
                    margin={{ top: 10, bottom: 68, left: 5, right: 5 }}
                    slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } } }}
                    series={[
                      {
                        innerRadius: 62,
                        outerRadius: 112,
                        paddingAngle: 1,
                        cornerRadius: 3,
                        arcLabelMinAngle: 20,
                        arcLabel: (item) => `${((item.value / breakdownTotal) * 100).toFixed(1)}%`,
                        data: breakdown.map((b, i) => ({
                          id: i,
                          value: b.total,
                          label: b.category,
                          color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                        })),
                      },
                    ]}
                    sx={{ "& .MuiPieArcLabel-root": { fill: "#fff", fontWeight: 700, fontSize: 12 } }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Done</TableCell>
                  <TableCell align="right">In Progress</TableCell>
                  <TableCell align="right">Backlog</TableCell>
                  <TableCell align="right">Resolution %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdown.map((b) => (
                  <TableRow key={b.category}>
                    <TableCell>{b.category}</TableCell>
                    <TableCell align="right">{b.total}</TableCell>
                    <TableCell align="right">{b.done}</TableCell>
                    <TableCell align="right">{b.inProgress}</TableCell>
                    <TableCell align="right">{b.backlog}</TableCell>
                    <TableCell align="right">{b.resolutionPct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom>
            Bug Assignment by Developer
          </Typography>
          {summary && (
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6} md={3}>
                <KpiTile label="Devs with Bugs" value={summary.devsWithBugs} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiTile label="Assigned to Dev" value={summary.assignedToDev} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiTile label="Pending Assignment" value={summary.pendingAssignment} />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiTile label="No Code Change" value={summary.noCodeChange} />
              </Grid>
            </Grid>
          )}

          {ranking.length > 0 && (
            <Grid container spacing={2} mb={2}>
              {ranking.slice(0, 3).map((r, i) => (
                <Grid item xs={12} md={4} key={r.developer}>
                  <KpiTile label={`${MEDALS[i]} ${r.developer}`} value={`${r.total} bugs`} delta={r.resolutionPct} deltaSuffix="% resolved" />
                </Grid>
              ))}
            </Grid>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560, mb: 3 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Developer</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Done</TableCell>
                  <TableCell align="right">In Progress</TableCell>
                  <TableCell align="right">Backlog</TableCell>
                  <TableCell align="right">Open Load</TableCell>
                  <TableCell align="right">Resolution %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranking.map((r) => (
                  <TableRow key={r.developer}>
                    <TableCell>{r.rank}</TableCell>
                    <TableCell>{r.developer}</TableCell>
                    <TableCell align="right">{r.total}</TableCell>
                    <TableCell align="right">{r.done}</TableCell>
                    <TableCell align="right">{r.inProgress}</TableCell>
                    <TableCell align="right">{r.backlog}</TableCell>
                    <TableCell align="right">{r.open}</TableCell>
                    <TableCell align="right">{r.resolutionPct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {ranking.length > 0 && (
            <Paper variant="outlined" sx={{ ...CHART_CARD_SX, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Workload Breakdown
              </Typography>
              <BarChart
                height={Math.max(280, 130 + 40 * ranking.length)}
                layout="horizontal"
                yAxis={[{ scaleType: "band", data: ranking.map((r) => r.developer ?? "") }]}
                series={[
                  { data: ranking.map((r) => r.done ?? 0), label: "Done", stack: "w", color: "#16a34a" },
                  { data: ranking.map((r) => r.backlog ?? 0), label: "Backlog", stack: "w", color: "#ca8a04" },
                  { data: ranking.map((r) => r.inProgress ?? 0), label: "In Progress", stack: "w", color: "#0284c7" },
                ]}
                margin={{ left: 150, bottom: 85 }}
                slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } } }}
              />
            </Paper>
          )}

          {ranking.length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Bug Breakdown per Developer
              </Typography>
              {ranking.map((r) => {
                const devBugs = [...rows.filter((b) => b.assignee === r.developer)].sort(byCreatedDesc);
                return (
                  <Accordion key={r.developer} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        👤 {r.developer} — {devBugs.length} bug(s)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {devBugs.map((b) => bugLine(b))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}

          {pendingBugs.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>⚠️ Pending Assignment</AlertTitle>
              <Typography variant="body2" mb={1}>
                <b>{pendingBugs.length}</b> bug(s) with the Development subtask unassigned — needs triage
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {pendingBugs.map((b) => bugLine(b))}
              </Box>
            </Alert>
          )}
          {pendingBugs.length === 0 && ranking.length > 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              All bugs in this month are assigned to a developer
            </Alert>
          )}

          {noChangeBugs.length > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>🚫 No Code Change</AlertTitle>
              <Typography variant="body2" mb={1}>
                <b>{noChangeBugs.length}</b> bug(s) closed without code change (Development subtask cancelled)
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {noChangeBugs.map((b) => bugLine(b))}
              </Box>
            </Alert>
          )}

          {compareMonths.length > 0 && trend && trendCats.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Category Trend — Monthly Comparison
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      {trendMonths.map((mk) => (
                        <TableCell key={mk} align="right">
                          {formatMonthKey(mk)}
                        </TableCell>
                      ))}
                      <TableCell align="right">Δ Change</TableCell>
                      <TableCell align="right">% Change</TableCell>
                      <TableCell align="center">Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(trend.rows ?? []).map((r) => (
                      <TableRow
                        key={r.isTotal ? "__total" : r.category}
                        sx={r.isTotal ? { bgcolor: "action.hover", "& td": { fontWeight: 700 } } : undefined}
                      >
                        <TableCell>{r.isTotal ? "TOTAL" : r.category}</TableCell>
                        {trendMonths.map((mk) => (
                          <TableCell key={mk} align="right">
                            {r.countsByMonth?.[mk] ?? 0}
                          </TableCell>
                        ))}
                        <TableCell align="right">{(r.delta ?? 0) > 0 ? `+${r.delta}` : r.delta}</TableCell>
                        <TableCell align="right">{(r.pctChange ?? 0) > 0 ? `+${r.pctChange}` : r.pctChange}%</TableCell>
                        <TableCell align="center">{TREND_ICON[r.trend ?? ""] ?? r.trend}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Paper variant="outlined" sx={{ ...CHART_CARD_SX, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Bug Count by Category per Month
                </Typography>
                <BarChart
                  height={320}
                  xAxis={[{ scaleType: "band", data: trendCats.map((r) => r.category ?? "") }]}
                  series={trendMonths.map((mk) => ({
                    label: formatMonthKey(mk),
                    data: trendCats.map((r) => r.countsByMonth?.[mk] ?? 0),
                  }))}
                  margin={{ left: 40, bottom: 70 }}
                  slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } } }}
                />
              </Paper>

              {insights.length > 0 && (
                <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>💡 Insights</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {insights.map((r) => {
                      const pct = r.pctChange ?? 0;
                      return (
                        <Box component="li" key={r.category}>
                          {pct > 0 ? "🔺" : "🔻"} <b>{r.category}</b> {pct > 0 ? "increased" : "dropped"} {Math.abs(pct)}% —{" "}
                          {pct > 0 ? "may need attention" : "improvement noted"}
                        </Box>
                      );
                    })}
                  </Box>
                </Alert>
              )}
            </>
          )}

          {compareMonths.length > 0 && resMonths.length > 0 && resCats.length > 0 && (
            <Paper variant="outlined" sx={{ ...CHART_CARD_SX, mb: 4 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Monthly Resolution Rate by Category
              </Typography>
              <LineChart
                height={340}
                xAxis={[{ scaleType: "point", data: resMonths, valueFormatter: (v: string) => formatMonthKey(v) }]}
                series={resCats.map((c) => ({
                  label: c,
                  data: resMonths.map((m) => resLookup[c]?.[m] ?? null),
                  connectNulls: true,
                }))}
                margin={{ left: 50, bottom: 70 }}
                slotProps={{ legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } } }}
              />
            </Paper>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 4, mb: 1 }}>
            <Typography variant="h6">All Bugs — {data.monthLabel}</Typography>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={downloadBugsCsv} disabled={displayRows.length === 0}>
              Download CSV
            </Button>
          </Stack>
          <TextField
            size="small"
            fullWidth
            placeholder="🔍 Search bugs by key, summary or assignee…"
            value={bugSearch}
            onChange={(e) => setBugSearch(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Resolved</TableCell>
                  <TableCell>Assignee</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.map((b) => (
                  <TableRow key={b.key}>
                    <TableCell>
                      <Link href={safeHttpUrl(b.jiraUrl)} target="_blank" rel="noopener">
                        {b.key}
                      </Link>
                    </TableCell>
                    <TableCell>{b.summary}</TableCell>
                    <TableCell>{b.category}</TableCell>
                    <TableCell>{b.status}</TableCell>
                    <TableCell>{b.priority}</TableCell>
                    <TableCell>{fmtDate(b.created)}</TableCell>
                    <TableCell>{fmtDate(b.resolved)}</TableCell>
                    <TableCell>{b.assignee}</TableCell>
                  </TableRow>
                ))}
                {displayRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" align="center" py={2}>
                        No bugs match “{bugSearch}”.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default BugsTab;
